/**
 * The agent's live step list for the open chat — render state only.
 *
 * Deliberately dumb. The list's lifecycle (when it is created, advanced,
 * replaced, and settled) belongs to the server runtime, which is the only
 * participant that knows why a turn ended and can persist the answer
 * (`packages/ai/src/core/plan/`). An earlier cut split that ownership between
 * the model, this store, and `chat.tsx`, and the seam produced the bug that
 * prompted the rewrite: the client cleared the list on every user message, so
 * saying "continue" destroyed a plan the model still believed in, and nothing
 * ever brought it back.
 *
 * So there is no clearing, no settling, and no localStorage here. The store
 * holds what the server last said, and the components render it.
 *
 * @module store/plan-store
 */

import { create } from "zustand";

import type { PlanPayload } from "@/types/plan";

interface PlanState {
  /**
   * The current list, or null when the agent hasn't planned — the correct,
   * common state for a trivial request, not a missing one.
   */
  plan: PlanPayload | null;

  /**
   * Whether a turn is in flight in the open chat.
   *
   * The step list is live-run UI: it answers "what is the agent doing right
   * now". Once the turn ends that question has no answer, and a finished
   * checklist parked above the composer just competes with the reply the user
   * is actually reading. So the list is *rendered* only while a run is in
   * flight — the plan itself is kept (the server carries it into the next turn,
   * so "continue" resumes the same list rather than starting over).
   */
  running: boolean;

  /** Replace the rendered list. An empty list renders as nothing. */
  setPlan: (plan: PlanPayload | null) => void;

  /** Mirror the open chat's stream status. Owned by `chat.tsx`. */
  setRunning: (running: boolean) => void;
}

export const usePlanStore = create<PlanState>((set) => ({
  plan: null,
  running: false,
  setPlan: (plan) => set({ plan: plan && plan.tasks.length > 0 ? plan : null }),
  setRunning: (running) => set({ running }),
}));

/**
 * The list to render — the plan while the agent is working, nothing otherwise.
 *
 * Every surface reads through this rather than `plan` directly, so the chat card
 * and the CoWork panel cannot disagree about when the checklist is on screen.
 */
export function useLivePlan(): PlanPayload | null {
  return usePlanStore((s) => (s.running ? s.plan : null));
}
